/**
 * The Submission Pack filter bar (Specs/2026-09-23-submission-pack-redesign-plan.md,
 * Task 3.4).
 *
 * A flat toolbar row: search, a status segmented control, a Mine toggle and a
 * Type dropdown. Controlled — the page owns `PackFilters` so the blocker chips
 * and the `?item=` deep link can set and clear the same state.
 */

import { Check, ChevronDown, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  DEFAULT_PACK_FILTERS,
  isPackFilterActive,
  type PackFilters,
  type PackStatusFilter,
  type PackTypeFilter,
} from '@/lib/pack-filters'
import { cn } from '@/lib/utils'
import type { SubmissionPackRollup } from '@/lib/api'
import { PACK_STATUS_LABELS } from '@/lib/pack-readiness'

interface PackFilterBarProps {
  filters: PackFilters
  onChange: (filters: PackFilters) => void
  rollup: SubmissionPackRollup
  /** Every live item — "All" counts these, and the scope line's denominator. */
  totalCount: number
  /** Items left after filtering — the scope line's numerator. */
  shownCount: number
  /** Items owned by the current user. */
  mineCount: number
  /** Hides the Mine toggle when there is no signed-in user to match on. */
  canFilterMine: boolean
}

const TYPE_OPTIONS: Array<{ value: PackTypeFilter; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'response', label: 'Responses' },
  { value: 'form', label: 'Forms' },
  { value: 'attachment', label: 'Attachments' },
  { value: 'acknowledge', label: 'Acknowledgements' },
]

export function PackFilterBar({
  filters,
  onChange,
  rollup,
  totalCount,
  shownCount,
  mineCount,
  canFilterMine,
}: PackFilterBarProps) {
  const statusOptions: Array<{ value: PackStatusFilter; label: string; count: number }> = [
    { value: 'all', label: 'All', count: totalCount },
    { value: 'todo', label: PACK_STATUS_LABELS.not_ready, count: rollup.notReady },
    { value: 'in_progress', label: PACK_STATUS_LABELS.in_progress, count: rollup.inProgress },
    { value: 'ready', label: PACK_STATUS_LABELS.ready, count: rollup.ready },
  ]
  const typeLabel =
    TYPE_OPTIONS.find((option) => option.value === filters.type)?.label ?? 'All types'
  const isActive = isPackFilterActive(filters)

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] max-w-xs flex-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            aria-label="Search pack items"
            placeholder="Search items"
            value={filters.search}
            onChange={(event) => onChange({ ...filters, search: event.target.value })}
            className="border-transparent bg-muted pl-9"
          />
        </div>

        <div
          role="group"
          aria-label="Filter by status"
          className="inline-flex h-9 items-center gap-1 rounded-lg bg-muted p-1"
        >
          {statusOptions.map((option) => {
            // The over-limit mode is a status lens of its own, so no segment
            // claims to be selected while it is on.
            const isSelected = !filters.overLimit && filters.status === option.value
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={isSelected}
                onClick={() => onChange({ ...filters, status: option.value, overLimit: false })}
                className={cn(
                  'flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-sm transition-colors',
                  isSelected
                    ? 'bg-card font-medium text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {option.label}
                <span className="text-meta tabular-nums">{option.count}</span>
              </button>
            )
          })}
        </div>

        {canFilterMine && (
          <Button
            type="button"
            variant="outline"
            aria-pressed={filters.mine}
            onClick={() => onChange({ ...filters, mine: !filters.mine })}
            className={cn(
              'gap-1.5',
              filters.mine &&
                'border-[hsl(var(--brand))] bg-[hsl(var(--brand)/0.06)] hover:bg-[hsl(var(--brand)/0.1)]'
            )}
          >
            Mine
            <span className="text-meta tabular-nums">{mineCount}</span>
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" aria-label={`Type: ${typeLabel}`} className="gap-1.5">
              {typeLabel}
              <ChevronDown aria-hidden className="h-4 w-4 text-subtle" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {TYPE_OPTIONS.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => onChange({ ...filters, type: option.value })}
                className="gap-2"
              >
                <Check
                  aria-hidden
                  className={cn(
                    'h-4 w-4',
                    filters.type === option.value ? 'opacity-100' : 'opacity-0'
                  )}
                />
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isActive && (
        <p className="text-meta" aria-live="polite">
          Showing {shownCount} of {totalCount}
          {filters.overLimit && ' over the word limit'}
          {' · '}
          <button
            type="button"
            onClick={() => onChange(DEFAULT_PACK_FILTERS)}
            className="text-[hsl(var(--link))] hover:underline"
          >
            Clear
          </button>
        </p>
      )}
    </div>
  )
}
